import { useState } from 'react'
import { useParams } from 'react-router'

import { isPrivilegedRole } from '@ankor-io/common/domain/iam/account'
import {
  Badge,
  Button,
  Divider,
  Heading,
  Loading,
  Switch,
  TabContent,
  TabItem,
  Tabs,
  Text,
} from '@ankor-io/ui-components'

import { OrganizationFeatureFlags } from '@/components/organizations/feature-flags/organization-feature-flags'
import { MIN_LOADING_DISPLAY_MS } from '@/constants/ui'
import { useMinimumDisplayTime } from '@/hooks/useMinimumDisplayTime'
import { useUser } from '@/hooks/useUser'
import { useOrganizationDetailsQuery } from '@/queries'
import { getOrganizationDisplayDescription } from '@/utils/organization'

export function OrganizationDetails() {
  const { id } = useParams<{ id: string }>()
  const [isAddToggleDialogOpen, setIsAddToggleDialogOpen] = useState(false)
  const { data: organization, error, isPending } = useOrganizationDetailsQuery(id, { enabled: !!id })
  const { activeOrganizationRole } = useUser()
  const showLoader = useMinimumDisplayTime(isPending, MIN_LOADING_DISPLAY_MS)

  // Error toast is handled globally by QueryProvider
  // Component still handles error UI state (error display below)

  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <Text className="text-red-600">Organization ID is required</Text>
        </div>
      </div>
    )
  }

  if (showLoader) {
    return <Loading variant="spinner" />
  }

  if (error) {
    return <div>Error: {error.message}</div>
  }

  const handleVisibilityToggle = () => {
    //!!TODO: Implement visibility toggle mutation
  }

  return (
    <div className="space-y-4 relative min-h-[calc(100vh-7.5rem)] pb-10">
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <>
            <Heading level={2}>{organization?.name}</Heading>
          </>
          <div className="flex items-center gap-2">
            {isPrivilegedRole(activeOrganizationRole) && (
              <>
                <Button
                  color="primary"
                  onClick={() => setIsAddToggleDialogOpen(true)}
                >
                  New Feature Flag
                </Button>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Id:</span>
            <Badge color="gray">{organization?.id}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Users:</span>
            <Badge color="gray">{organization?.userCount || 0}</Badge>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-900 dark:text-white">Visibility:</span>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {organization?.visibility === 'visible' ? 'Visible' : 'Hidden'}
              </span>
              <Switch
                checked={organization?.visibility === 'visible'}
                onChange={handleVisibilityToggle}
              />
            </div>
          </div>
        </div>

        <Text className="max-w-2xl text-gray-600 dark:text-gray-400">
          {getOrganizationDisplayDescription(organization)}
        </Text>

        <Divider />
      </div>

      <Tabs>
        <TabItem
          current={false}
          to={`/organizations/${id}/vessels`}
        >
          Vessels
        </TabItem>
        <TabItem
          current={true}
          to={`/organizations/${id}`}
        >
          Feature Flags
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/pricing`}
        >
          Pricing
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/integrations`}
        >
          Integrations
        </TabItem>
        <TabItem
          current={false}
          to={`/organizations/${id}/pipelines`}
        >
          Pipelines
        </TabItem>
      </Tabs>

      <TabContent isActive={true}>
        <OrganizationFeatureFlags
          isDialogOpen={isAddToggleDialogOpen}
          setIsDialogOpen={setIsAddToggleDialogOpen}
        />
      </TabContent>
    </div>
  )
}

export default OrganizationDetails
